/**
 * Commitment Detail Report
 * 
 * Shows commitment (purchase order) lines with their billing status.
 * Each commitment line is a summary row; bill detail rows are grouped
 * underneath and collapsed by default (Excel row grouping).
 * 
 * Data model:
 *   - PODOCUMENTENTRY = commitment lines (orders, subcontracts, POs)
 *     - Original lines: PRIMARYDOCLINEKEY = RECORDNO (self-referencing)
 *     - Invoice/change lines: PRIMARYDOCLINEKEY → original line's RECORDNO
 *   - APBILLITEM.PRIMARYDOCLINEKEY → PODOCUMENTENTRY.RECORDNO
 *     - Links each bill line back to the original commitment line
 * 
 * Columns:
 *   Project, Cost Code, Cost Type, Vendor, Order, Order Date, Order Line #,
 *   Original Amount, Approved Changes, Revised Amount, Amount Billed,
 *   Retainage Held, Retainage Released, Amount Paid, Balance
 * 
 * The "Amount Billed" cell on summary rows is the total; detail rows
 * underneath show individual bills with hyperlinks to Intacct.
 * 
 * Row grouping: detail rows are at outline level 2, summary rows at level 1.
 * Groups are collapsed by default so users click + to expand.
 */

import { toIntacctDate } from './reportHelpers';

const EXCLUDED_STATES = ['Reversed', 'Reversal', 'Voided', 'Deleted'];

const commitmentDetailReport = {
  id: 'commitment-detail',
  name: 'Commitment Detail Report',
  description: 'Purchase order commitment lines with original, revised, billed, paid, and balance amounts. Bill details are grouped under each commitment line — click + to expand and see individual bills with Intacct links.',
  category: 'Purchasing',

  parameters: [
    {
      id: 'projectFilter', label: 'Project ID', type: 'text',
      default: '', description: 'Optional: filter to a specific project (leave blank for all)'
    },
    {
      id: 'vendorFilter', label: 'Vendor ID', type: 'text',
      default: '', description: 'Optional: filter to a specific vendor (leave blank for all)'
    },
    {
      id: 'includeFullyBilled', label: 'Include Fully Billed/Closed', type: 'checkbox',
      default: true, description: 'Show commitment lines where the full amount has been billed'
    },
    {
      id: 'includeClosed', label: 'Include Closed Orders', type: 'checkbox',
      default: false, description: 'Show commitment lines from closed purchase orders'
    }
  ],

  queries: [
    {
      // Original commitment lines only (PRIMARYDOCLINEKEY = RECORDNO)
      id: 'commitmentLines', object: 'PODOCUMENTENTRY',
      fields: [
        'RECORDNO', 'DOCHDRNO', 'DOCHDRID', 'DOCHDRNO', 'LINE_NO',
        'PROJECTID', 'PROJECTNAME',
        'COSTTYPEID', 'COSTTYPENAME', 'TASKID', 'TASKNAME',
        'VENDORID', 'VENDORNAME',
        'ITEMID', 'ITEMNAME', 'ITEMDESC', 'MEMO',
        'TRX_VALUE', 'TRX_REVISEDVALUE',
        'POSTEDCHANGEEXTPRICE', 'DRAFTCHANGEPRICE',
        'TOTAL_AMOUNT_CONVERTED', 'TOTAL_AMOUNT_REMAINING',
        'TRX_AMOUNTRETAINED', 'AMOUNTRETAINED',
        'PRICE_CONVERTED', 'QTY_CONVERTED', 'QTY_REMAINING',
        'PRIMARYDOCKEY', 'PRIMARYDOCLINEKEY',
        'STATE', 'STATUS', 'ADDEDBYCHANGE',
        'RECORD_URL', 'WHENCREATED'
      ],
      buildFilter: (params) => {
        // Fetch all lines; we filter to originals (PRIMARYDOCLINEKEY = RECORDNO) in process()
        let f = `RECORDNO > 0`;
        if (params.projectFilter?.trim()) f += ` AND PROJECTID = '${params.projectFilter.trim()}'`;
        if (params.vendorFilter?.trim()) f += ` AND VENDORID = '${params.vendorFilter.trim()}'`;
        return f;
      }
    },
    {
      // PO document headers for order date and status
      id: 'orderHeaders', object: 'PODOCUMENT',
      fields: [
        'RECORDNO', 'DOCID', 'DOCNO', 'PONUMBER',
        'CUSTVENDID', 'CUSTVENDNAME',
        'PROJECT', 'PROJECTNAME',
        'WHENCREATED', 'STATE', 'STATUS',
        'ORIGINALAMOUNT', 'REVISEDTOTAL', 'POSTEDCHANGESTOTAL',
        'TOTALENTERED', 'TOTALDUE', 'TOTALPAID',
        'MEGAENTITYID', 'RECORD_URL'
      ],
      buildFilter: (params) => {
        let f = `RECORDNO > 0`;
        if (params.projectFilter?.trim()) f += ` AND PROJECT = '${params.projectFilter.trim()}'`;
        if (params.vendorFilter?.trim()) f += ` AND CUSTVENDID = '${params.vendorFilter.trim()}'`;
        return f;
      }
    },
    {
      // Bill line items linked to PO lines
      id: 'billLines', object: 'APBILLITEM',
      fields: [
        'RECORDNO', 'APBILL.RECORDNO', 'APBILL.RECORDID', 'APBILL.DOCNUMBER',
        'APBILL.RECORD_URL', 'APBILL.MEGAENTITYID',
        'LINE_NO', 'AMOUNT', 'TRX_AMOUNT',
        'TOTALPAID', 'TRX_TOTALPAID',
        'TRX_AMOUNTRETAINED', 'TRX_AMOUNTRELEASED',
        'PRIMARYDOCKEY', 'PRIMARYDOCLINEKEY',
        'PROJECTID', 'COSTTYPEID', 'TASKID',
        'VENDORID', 'VENDORNAME',
        'ENTRYDESCRIPTION',
        'STATE', 'RECORD_URL',
        'WHENCREATED'
      ],
      buildFilter: (params) => {
        // Only bill lines linked to POs
        let f = `PRIMARYDOCLINEKEY > 0`;
        if (params.projectFilter?.trim()) f += ` AND PROJECTID = '${params.projectFilter.trim()}'`;
        if (params.vendorFilter?.trim()) f += ` AND VENDORID = '${params.vendorFilter.trim()}'`;
        return f;
      }
    }
  ],

  additionalQueries: [],
  optionalColumns: [],

  process: (datasets, params) => {
    const commitmentLines = (datasets.commitmentLines || []).filter(cl => {
      // Original lines only: PRIMARYDOCLINEKEY equals own RECORDNO
      const rn = String(cl.RECORDNO || '').trim();
      const pdlk = String(cl.PRIMARYDOCLINEKEY || '').trim();
      return rn && pdlk && rn === pdlk;
    });
    const orderHeaders = datasets.orderHeaders || [];
    const billLines = datasets.billLines || [];

    const includeClosed = params.includeClosed;
    const includeFullyBilled = params.includeFullyBilled;

    // Build order header lookup: RECORDNO → header
    const orderMap = {};
    for (const oh of orderHeaders) {
      orderMap[String(oh.RECORDNO || '').trim()] = oh;
    }

    // Build bill lines grouped by PRIMARYDOCLINEKEY → commitment line RECORDNO
    const billsByCommitLine = {};
    for (const bl of billLines) {
      const state = String(bl.STATE || '').trim();
      if (EXCLUDED_STATES.includes(state)) continue;

      const clKey = String(bl.PRIMARYDOCLINEKEY || '').trim();
      if (!clKey || clKey === '0') continue;

      if (!billsByCommitLine[clKey]) billsByCommitLine[clKey] = [];
      billsByCommitLine[clKey].push(bl);
    }

    // Process commitment lines
    const r2 = (v) => Math.round(v * 100) / 100;
    const reportRows = [];
    const detailRowIndices = []; // track which rows are detail rows for grouping

    // Sort commitment lines by project, task/cost code, ORDER, then vendor
    // This ensures all lines from PO-1 appear before PO-2 within the same cost code
    const sortedLines = [...commitmentLines].sort((a, b) => {
      const cmp1 = (a.PROJECTID || '').localeCompare(b.PROJECTID || '');
      if (cmp1 !== 0) return cmp1;
      const cmp2 = (a.TASKID || '').localeCompare(b.TASKID || '');
      if (cmp2 !== 0) return cmp2;
      const cmp3 = (a.DOCHDRID || '').localeCompare(b.DOCHDRID || '');
      if (cmp3 !== 0) return cmp3;
      const cmp4 = (a.VENDORNAME || '').localeCompare(b.VENDORNAME || '');
      if (cmp4 !== 0) return cmp4;
      // Within same order, sort by line number
      return (parseInt(a.LINE_NO, 10) || 0) - (parseInt(b.LINE_NO, 10) || 0);
    });

    let totalOriginal = 0, totalChanges = 0, totalRevised = 0;
    let totalBilled = 0, totalRetHeld = 0, totalRetReleased = 0, totalPaid = 0, totalBalance = 0;

    for (const cl of sortedLines) {
      const clRecordNo = String(cl.RECORDNO || '').trim();
      const orderKey = String(cl.DOCHDRNO || '').trim();
      const order = orderMap[orderKey];

      // Filter closed orders
      const orderState = order ? String(order.STATE || '').trim() : '';
      if (!includeClosed && orderState === 'Closed') continue;

      const originalAmt = r2(parseFloat(cl.TRX_VALUE) || 0);
      const approvedChanges = r2(parseFloat(cl.POSTEDCHANGEEXTPRICE) || 0);
      const revisedAmt = r2(parseFloat(cl.TRX_REVISEDVALUE) || 0);

      // Sum bills against this commitment line
      const bills = billsByCommitLine[clRecordNo] || [];
      let amtBilled = 0, retHeld = 0, retReleased = 0, amtPaid = 0;
      for (const bl of bills) {
        amtBilled += parseFloat(bl.AMOUNT) || 0;
        retHeld += parseFloat(bl.TRX_AMOUNTRETAINED) || 0;
        retReleased += parseFloat(bl.TRX_AMOUNTRELEASED) || 0;
        amtPaid += parseFloat(bl.TOTALPAID) || 0;
      }
      amtBilled = r2(amtBilled);
      retHeld = r2(retHeld);
      retReleased = r2(retReleased);
      amtPaid = r2(amtPaid);

      // Use PO line retainage if no bills yet
      if (retHeld === 0 && cl.TRX_AMOUNTRETAINED) {
        retHeld = r2(parseFloat(cl.TRX_AMOUNTRETAINED) || 0);
      }

      const balance = r2(revisedAmt - amtBilled - retHeld);

      if (!includeFullyBilled && balance === 0 && (retHeld - retReleased) <= 0) continue;

      totalOriginal += originalAmt;
      totalChanges += approvedChanges;
      totalRevised += revisedAmt;
      totalBilled += amtBilled;
      totalRetHeld += retHeld;
      totalRetReleased += retReleased;
      totalPaid += amtPaid;
      totalBalance += balance;

      // Order info
      const orderDocId = order ? (order.DOCNO || order.DOCID || '') : (cl.DOCHDRID || '');
      const orderDate = order ? (order.WHENCREATED || '') : (cl.WHENCREATED || '');
      const orderUrl = order ? (order.RECORD_URL || '') : '';
      const orderEntity = order ? (order.MEGAENTITYID || '') : '';

      // Project display: "ID--Name"
      const projectDisplay = cl.PROJECTID
        ? (cl.PROJECTNAME ? `${cl.PROJECTID}--${cl.PROJECTNAME}` : cl.PROJECTID)
        : '';

      // Line number: Intacct stores line 1 as 0, so add 1
      const rawLineNo = parseInt(cl.LINE_NO, 10);
      const lineNo = !isNaN(rawLineNo) ? rawLineNo + 1 : '';

      // Summary row — Description links to order (same as Order column)
      const summaryRow = {
        'Project': projectDisplay,
        'Cost Code': cl.TASKID || '',
        'Cost Code Name': cl.TASKNAME || '',
        'Cost Type': cl.COSTTYPEID || '',
        'Vendor': cl.VENDORNAME || '',
        'Vendor ID': cl.VENDORID || '',
        'Order': orderDocId,
        'Order Date': orderDate,
        'Line #': lineNo,
        'Description': cl.ITEMDESC || cl.MEMO || '',
        'Original Amount': originalAmt,
        'Approved Changes': approvedChanges,
        'Revised Amount': revisedAmt,
        'Amount Billed': amtBilled,
        'Retainage Held': retHeld,
        'Retainage Released': retReleased,
        'Amount Paid': amtPaid,
        'Balance': balance,
        '_orderUrl': orderUrl,
        '_orderEntity': orderEntity,
        '_descUrl': orderUrl,
        '_descEntity': orderEntity,
        '_isDetail': false,
        '_hasBills': bills.length > 0,
      };

      // Summary row first — ExcelJS sets summaryBelow:false so +/- appears here
      reportRows.push(summaryRow);

      // Detail rows (one per bill) — AFTER summary, expand downward
      if (bills.length > 0) {
        const sortedBills = [...bills].sort((a, b) => {
          const da = a.WHENCREATED || '';
          const db = b.WHENCREATED || '';
          return da.localeCompare(db);
        });

        for (const bl of sortedBills) {
          const billUrl = bl['APBILL.RECORD_URL'] || bl.RECORD_URL || '';
          const billEntity = bl['APBILL.MEGAENTITYID'] || '';

          reportRows.push({
            'Project': '',
            'Cost Code': '',
            'Cost Code Name': '',
            'Cost Type': '',
            'Vendor': '',
            'Vendor ID': '',
            'Order': '',
            'Order Date': '',
            'Line #': '',
            'Description': `  ↳ ${bl['APBILL.RECORDID'] || bl['APBILL.DOCNUMBER'] || 'Bill'}${bl.ENTRYDESCRIPTION ? ' — ' + bl.ENTRYDESCRIPTION : ''}`,
            'Original Amount': '',
            'Approved Changes': '',
            'Revised Amount': '',
            'Amount Billed': r2(parseFloat(bl.AMOUNT) || 0),
            'Retainage Held': r2(parseFloat(bl.TRX_AMOUNTRETAINED) || 0),
            'Retainage Released': r2(parseFloat(bl.TRX_AMOUNTRELEASED) || 0),
            'Amount Paid': r2(parseFloat(bl.TOTALPAID) || 0),
            'Balance': '',
            '_orderUrl': '',
            '_orderEntity': '',
            '_descUrl': billUrl,
            '_descEntity': billEntity,
            '_isDetail': true,
            '_hasBills': false,
          });
          detailRowIndices.push(reportRows.length - 1);
        }
      }
    }

    // Grand total row
    reportRows.push({
      'Project': 'TOTAL',
      'Cost Code': '', 'Cost Code Name': '', 'Cost Type': '',
      'Vendor': '', 'Vendor ID': '',
      'Order': '', 'Order Date': '', 'Line #': '',
      'Description': '',
      'Original Amount': r2(totalOriginal),
      'Approved Changes': r2(totalChanges),
      'Revised Amount': r2(totalRevised),
      'Amount Billed': r2(totalBilled),
      'Retainage Held': r2(totalRetHeld),
      'Retainage Released': r2(totalRetReleased),
      'Amount Paid': r2(totalPaid),
      'Balance': r2(totalBalance),
      '_orderUrl': '',
      '_orderEntity': '',
      '_descUrl': '',
      '_descEntity': '',
      '_isDetail': false,
      '_hasBills': false,
    });

    const columns = [
      'Project', 'Cost Code', 'Cost Code Name', 'Cost Type',
      'Vendor', 'Vendor ID',
      'Order', 'Order Date', 'Line #', 'Description',
      'Original Amount', 'Approved Changes', 'Revised Amount',
      'Amount Billed', 'Retainage Held', 'Retainage Released',
      'Amount Paid', 'Balance'
    ];

    const commitCount = reportRows.filter(r => !r._isDetail && r.Project !== 'TOTAL').length;
    const billCount = detailRowIndices.length;
    const projectCount = new Set(reportRows.filter(r => !r._isDetail && r.Project && r.Project !== 'TOTAL').map(r => r.Project)).size;

    const summaryLines = [
      `${commitCount} commitment line(s) across ${projectCount} project(s)`,
      `${billCount} bill detail row(s) (expandable)`,
      `Total Committed: $${r2(totalRevised).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      `Total Billed: $${r2(totalBilled).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      `Total Paid: $${r2(totalPaid).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      `Remaining Balance: $${r2(totalBalance).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
    ];

    return {
      columns,
      data: reportRows,
      summary: { lines: summaryLines },
      detailRowIndices,
      formatRules: {
        'Original Amount': { type: 'currency', decimals: 2 },
        'Approved Changes': { type: 'currency', decimals: 2 },
        'Revised Amount': { type: 'currency', decimals: 2 },
        'Amount Billed': { type: 'currency', decimals: 2 },
        'Retainage Held': { type: 'currency', decimals: 2 },
        'Retainage Released': { type: 'currency', decimals: 2 },
        'Amount Paid': { type: 'currency', decimals: 2 },
        'Balance': { type: 'currency', decimals: 2 },
        'Order': { type: 'hyperlink', urlKey: '_orderUrl', entityKey: '_orderEntity' },
        'Description': { type: 'hyperlink', urlKey: '_descUrl', entityKey: '_descEntity' },
      }
    };
  },
};

export default commitmentDetailReport;